import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { DownloadOutlined, ProfileOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import HttpService from "../../services/httpService";
import { ROLENAME } from "../../utils/index";

const ALLOWED_ROLES = [ROLENAME.OperationDesignManager, ROLENAME.AGM_Design];
const DEFAULT_START = "2022-01-01";
const getCurrentDate = () => new Date().toISOString().split("T")[0];

const PERFORMANCE_COLORS = {
    Gold: { backgroundColor: "#fff3e0", color: "#ff6f00" },
    Green: { backgroundColor: "#e8f5e8", color: "#2e7d32" },
    Yellow: { backgroundColor: "#fffde7", color: "#7c6f00" },
    Red: { backgroundColor: "#ffebee", color: "#c62828" },
};

// Columns whose value is a { count, data } object — clickable to open the standard modal
const CLICKABLE_KEYS = ["dkoInternal", "dkoCustomer", "design", "holdApproved", "holdPending", "completed", "lost", "total"];
// Columns whose value is a { value, data } object — clickable, but display the average, not a count
const AVERAGE_CLICKABLE_KEYS = ["npsDesign", "npsPostDesign"];

const useStyles = makeStyles(() => ({
    pageWrapper: {
        padding: "2rem",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
        backgroundColor: "#fff",
        marginBottom: "1.5rem",
    },
    headerBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "26px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        background: "linear-gradient(135deg, #1C375D 0%, #2f5583 100%)",
        boxShadow: "0 6px 18px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.3px",
        fontSize: "28px",
    },
    titleIcon: {
        fontSize: "26px",
        color: "#F47E46",
    },
    subtitle: {
        display: "inline-block",
        marginTop: "12px",
        padding: "5px 14px",
        borderRadius: "20px",
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "12.5px",
        fontWeight: 600,
    },
    headerCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        border: "1px solid rgba(255,255,255,0.2)",
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    bodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
        whiteSpace: "nowrap",
    },
    grandTotalCell: {
        textAlign: "center",
        fontSize: "13px",
        fontWeight: 700,
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#1e293b",
        backgroundColor: "#f0f4f8",
        whiteSpace: "nowrap",
    },
    performanceBadge: {
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 700,
    },
    clickableValue: {
        fontWeight: 700,
        color: "#1C375D",
        cursor: "pointer",
        textDecoration: "underline",
        "&:hover": { color: "#F47E46" },
    },
    noData: {
        textAlign: "center",
        padding: "40px",
        color: "#94a3b8",
        fontSize: "15px",
    },
    submitButton: {
        backgroundColor: "#F47E46 !important",
        color: "white !important",
        "&:hover": { backgroundColor: "#E0663D !important" },
    },
    clearButton: {
        backgroundColor: "#f7b7b7 !important",
        color: "#151921 !important",
    },
    // Modal table styles
    modalHeaderCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.2)",
        borderBottom: "1px solid #fff",
        position: "sticky",
        top: 0,
        zIndex: 2,
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    modalBodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    modalRowEven: { backgroundColor: "#f9f9f9" },
    modalRowOdd: { backgroundColor: "#fff" },
    linkStyle: {
        textDecoration: "underline",
        color: "#1C375D",
        fontWeight: 700,
        "&:hover": { color: "#152642" },
    },
    csv: {
        display: "flex",
        justifyContent: "flex-end",
        margin: "16px 0",
    },
}));

const COLS = [
    { key: "monthYear", label: "Month/Year" },
    { key: "dkoInternal", label: "DKO Internal" },
    { key: "dkoCustomer", label: "DKO-Customer" },
    { key: "design", label: "Design" },
    { key: "holdApproved", label: "Hold-Approved" },
    { key: "holdPending", label: "Hold Pending" },
    { key: "completed", label: "Completed" },
    { key: "completedValue", label: "Completed Value" },
    { key: "lost", label: "Lost" },
    { key: "npsDesign", label: "NPS-Design" },
    { key: "npsPostDesign", label: "NPS-Post Design" },
    { key: "ageingFromKOM", label: "Ave Ageing of Projects from KOM" },
    { key: "total", label: "Total" },
    { key: "performancePercent", label: "Performance of the month" },
    { key: "rewardsPoints", label: "Rewards Points Accumulated" },
];

const MODAL_COLS = [
    { label: "Sl. No.", key: "slno" },
    { label: "Customer Name", key: "customerName" },
    { label: "Project No.", key: "erpProjectNo" },
    { label: "Lead No.", key: "lead_no" },
    { label: "Value (INR L)", key: "value" },
    { label: "City", key: "cityName" },
    { label: "Exp. Ctr. Name", key: "experienceCenterName" },
    { label: "Current Stage", key: "currentStage" },
    { label: "NPS Design Score", key: "npsDesignScore" },
    { label: "NPS Post Design Score", key: "npsPostDesignScore" },
];

const formatCell = (value) => (value === null || value === undefined || value === "" ? "-" : value);

function normalizeRows(leads = []) {
    return leads.map((lead, idx) => ({ ...lead, slno: idx + 1 }));
}

export default function OdmAgmSummaryDashboard() {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const userRoleName = userDetails?.roles?.[0]?.name || "";
    const isAllowed = ALLOWED_ROLES.includes(userRoleName);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [grandTotal, setGrandTotal] = useState(null);

    const [filters, setFilters] = useState({
        startDate: DEFAULT_START,
        endDate: getCurrentDate(),
    });

    const [modal, setModal] = useState({ open: false, title: "", leads: [] });

    useEffect(() => {
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSummary = async (appliedFilters = filters) => {
        setLoading(true);
        try {
            const res = await HttpService.getOdmAgmSummaryDashboard(appliedFilters);
            if (res.data?.success) {
                setRows(res.data.rows || []);
                setGrandTotal(res.data.grandTotal || null);
            }
        } catch (err) {
            console.error("Error fetching summary dashboard:", err);
        }
        setLoading(false);
    };

    const handleFilterChange = (name, value) =>
        setFilters((prev) => ({ ...prev, [name]: value }));

    const applyFilters = () => fetchSummary(filters);

    const clearFilters = () => {
        const def = { startDate: DEFAULT_START, endDate: getCurrentDate() };
        setFilters(def);
        fetchSummary(def);
    };

    const openModal = (title, leads) => setModal({ open: true, title, leads: leads || [] });
    const closeModal = () => setModal({ open: false, title: "", leads: [] });

    const renderPerformanceCell = (row) => {
        if (row.performancePercent === null || row.performancePercent === undefined) return "-";
        return (
            <span className={classes.performanceBadge} style={PERFORMANCE_COLORS[row.performanceColor] || {}}>
                {row.performancePercent}%
            </span>
        );
    };

    const renderCell = (row, col) => {
        if (col.key === "performancePercent") return renderPerformanceCell(row);
        if (AVERAGE_CLICKABLE_KEYS.includes(col.key)) {
            const metric = row[col.key] || { value: null, data: [] };
            if (metric.value === null || metric.value === undefined) return "-";
            if (!metric.data || metric.data.length === 0) return metric.value;
            return (
                <span
                    className={classes.clickableValue}
                    onClick={() => openModal(`${row.monthYear} — ${col.label}`, metric.data)}
                >
                    {metric.value}
                </span>
            );
        }
        if (CLICKABLE_KEYS.includes(col.key)) {
            const metric = row[col.key] || { count: 0, data: [] };
            if (!metric.count) return metric.count ?? 0;
            return (
                <span
                    className={classes.clickableValue}
                    onClick={() => openModal(`${row.monthYear} — ${col.label}`, metric.data)}
                >
                    {metric.count}
                </span>
            );
        }
        return formatCell(row[col.key]);
    };

    const modalRows = normalizeRows(modal.leads);
    const modalCsvHeaders = MODAL_COLS.map((c) => ({ label: c.label, key: c.key }));

    if (userDetails?._id && !isAllowed) return <Redirect to="/unauthorized" />;

    return (
        <Paper className={classes.pageWrapper}>
            <div className={classes.headerBanner}>
                <Typography className={classes.title}>
                    <ProfileOutlined className={classes.titleIcon} />
                    My Summary Dashboard
                </Typography>
                <Typography className={classes.subtitle}>
                    Current overall summary — all your old projects, status and performance, month-wise. Click any count to view project details.
                </Typography>
            </div>

            <Paper elevation={2} style={{ padding: "16px", marginBottom: "24px" }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Sales Won Start Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.startDate}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: getCurrentDate() }}
                            onChange={(e) => handleFilterChange("startDate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Sales Won End Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.endDate}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => handleFilterChange("endDate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button variant="contained" className={classes.submitButton} onClick={applyFilters}>Submit</Button>
                            <Button variant="contained" className={classes.clearButton} onClick={clearFilters}>Clear</Button>
                        </div>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Typography className={classes.noData}>Loading...</Typography>
            ) : rows.length === 0 ? (
                <Typography className={classes.noData}>No data found for the selected filters</Typography>
            ) : (
                <TableContainer style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {COLS.map((c) => (
                                    <TableCell key={c.key} className={classes.headerCell}>{c.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.monthYear}>
                                    {COLS.map((c) => (
                                        <TableCell key={c.key} className={classes.bodyCell}>
                                            {renderCell(row, c)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {grandTotal && (
                                <TableRow>
                                    {COLS.map((c) => (
                                        <TableCell key={c.key} className={classes.grandTotalCell}>
                                            {renderCell(grandTotal, c)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── Detail Modal ───────────────────────────────────────────────── */}
            <Dialog open={modal.open} onClose={closeModal} maxWidth="xl" fullWidth
                PaperProps={{ style: { borderRadius: "10px", maxHeight: "90vh" } }}>
                <DialogTitle disableTypography style={{ backgroundColor: "#1C375D", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px" }}>
                    <Typography style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>
                        {modal.title}
                        <span style={{ marginLeft: 12, fontSize: "13px", fontWeight: 400, opacity: 0.8 }}>
                            ({modalRows.length} records)
                        </span>
                    </Typography>
                    <IconButton size="small" onClick={closeModal} style={{ color: "#fff" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: "16px 20px" }}>
                    {modalRows.length > 0 && (
                        <div className={classes.csv}>
                            <CSVLink data={modalRows} headers={modalCsvHeaders}
                                filename={`${modal.title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`}
                                style={{ textDecoration: "none" }}>
                                <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                    <DownloadOutlined /> Download CSV
                                </button>
                            </CSVLink>
                        </div>
                    )}
                    {modalRows.length === 0 ? (
                        <Typography className={classes.noData}>No data found</Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {MODAL_COLS.map((col) => (
                                            <TableCell key={col.key} className={classes.modalHeaderCell}>{col.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {modalRows.map((row, idx) => (
                                        <TableRow key={row.leadId || idx} className={idx % 2 === 0 ? classes.modalRowEven : classes.modalRowOdd}>
                                            {MODAL_COLS.map((col) => (
                                                <TableCell key={col.key} className={classes.modalBodyCell}>
                                                    {(col.key === "lead_no" || col.key === "erpProjectNo") && row.leadId ? (
                                                        <Link
                                                            to={`/project-logs/${row.leadId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={classes.linkStyle}
                                                        >
                                                            {row[col.key]}
                                                        </Link>
                                                    ) : row[col.key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}
